/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { useEffect, useRef, useState } from 'react'
import { TbarBg } from './icons.jsx'
import AssistantChat from './AssistantChat.jsx'

const PROGRAM_PAGES = ['/fitness-program/', '/cardio-program/', '/corrective-program/', '/flexibility-program/', '/mobility-program/', '/warm-cool-program/']
const ORDER_URL = 'https://app.sequoia.fitness/new-order'

const BASE_TABS = [
  { href: '/', icon: 'a/ico-dash-ico.webp', label: 'خانه', active: true },
  { href: '/#pricing', icon: 'a/ico-n-pro-ico-9457c689.webp', label: 'دریافت برنامه', scroll: 'pricing' },
  { assistant: true },
  { href: '/academy', icon: 'a/sequoia-academy-ico.webp', label: 'سکویا آکادمی' },
  { href: '/sequoia-tools/', icon: 'a/ico-calc-ico2-83a64147.webp', label: 'ابزارها' },
]

export default function TabBar() {
  const [onProgramPage, setOnProgramPage] = useState(false)
  const [inPricing, setInPricing] = useState(false)
  const [onToolsPage, setOnToolsPage] = useState(false)
  const TABS = onProgramPage
    ? BASE_TABS.map((t, i) =>
        i === 1 ? { ...t, href: ORDER_URL, scroll: undefined, active: true, orderPage: true } : i === 0 ? { ...t, active: false } : t,
      )
    : BASE_TABS.map((t, i) =>
        i === 0 ? { ...t, active: !inPricing && !onToolsPage } : i === 1 ? { ...t, active: inPricing } : i === 4 ? { ...t, active: onToolsPage } : t,
      )
  useEffect(() => {
    setOnProgramPage(PROGRAM_PAGES.some((p) => window.location.pathname.startsWith(p)))
    setOnToolsPage(window.location.pathname.startsWith('/sequoia-tools'))
  }, [])
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined
    let io = null
    let mo = null
    const attach = () => {
      const el = document.getElementById('pricing')
      if (!el) return false
      io = new IntersectionObserver((es) => es.forEach((e) => setInPricing(e.isIntersecting)), { rootMargin: '-45% 0px -45% 0px', threshold: 0 })
      io.observe(el)
      return true
    }
    if (!attach()) {
      mo = new MutationObserver(() => {
        if (attach()) mo.disconnect()
      })
      mo.observe(document.body, { childList: true, subtree: true })
    }
    return () => {
      if (io) io.disconnect()
      if (mo) mo.disconnect()
    }
  }, [])
  const [hidden, setHidden] = useState(false)
  const [phase, setPhase] = useState('closed')
  const phaseRef = useRef('closed')
  const timer = useRef(0)

  const setP = (p) => {
    phaseRef.current = p
    setPhase(p)
  }

  useEffect(() => {
    const footer = document.getElementById('seq-footer')
    if (!footer || !('IntersectionObserver' in window)) return undefined
    const io = new IntersectionObserver((es) => es.forEach((e) => setHidden(e.isIntersecting)), { root: null, threshold: 0 })
    io.observe(footer)
    return () => io.disconnect()
  }, [])

  const close = () => {
    if (phaseRef.current !== 'open') return
    clearTimeout(timer.current)
    setP('closing')
    timer.current = setTimeout(() => setP('closed'), 260)
  }
  const toggle = () => {
    clearTimeout(timer.current)
    if (phaseRef.current === 'open') close()
    else setP('open')
  }

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      clearTimeout(timer.current)
    }
  }, [])

  const open = phase === 'open'
  return (
    <>
      <div className={'seq-ast-bd' + (open ? ' is-open' : '')} id="seq-qm-bd" onClick={close}></div>
      <div className={'tbar' + (hidden ? ' is-hidden' : '')} id="seq-nav">
        <TbarBg />
        <nav className="tbar-wrap" dir="rtl" aria-label="دسترسی سریع">
          {TABS.map((t, i) =>
            t.assistant ? (
              <button key={i} className="tab-btn assist-btn" type="button" id="seq-qm-open" aria-label="پشتیبانی" aria-haspopup="dialog" aria-expanded={open ? 'true' : 'false'} aria-controls="seq-qm" onClick={toggle}>
                <div className="lev-mod">
                  <div className="bot-shadow"></div>
                  <div className="bot-stage">
                    <img src="a/ico-assistant-bot-07ba722e.webp" alt="" width="96" height="90" />
                  </div>
                </div>
              </button>
            ) : (
              <a
                key={t.href}
                className={'tab-btn' + (t.active ? ' active' : '')}
                href={t.href}
                target={t.orderPage ? '_blank' : undefined}
                rel={t.orderPage ? 'noreferrer' : undefined}
                onClick={
                  t.scroll
                    ? (e) => {
                        const el = document.getElementById(t.scroll)
                        if (!el) return
                        e.preventDefault()
                        setInPricing(true)
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      }
                    : undefined
                }
              >
                <div className="tab-icon">
                  <img src={t.icon} alt="" />
                </div>
                <span>{t.label}</span>
                <div className="tab-dot"></div>
              </a>
            ),
          )}
        </nav>
        <div className={'seq-ast' + (phase === 'open' ? ' is-open' : phase === 'closing' ? ' is-closing' : '')} id="seq-qm" role="dialog" aria-label="پشتیبانی" dir="rtl">
          <div className="seq-ast-card">
            <div className="seq-ast-head">
              <div className="seq-ast-prof">
                <div className="seq-ast-badge">
                  <img src="a/bot-ico.webp" alt="" />
                </div>
                <h3 className="seq-ast-name">پشتیبانی</h3>
              </div>
              <button className="seq-ast-x" type="button" id="seq-qm-close" aria-label="بستن" onClick={close}>
                ✕
              </button>
            </div>
            <AssistantChat />
          </div>
          <div className="seq-ast-tail">
            <div className="seq-ast-cone"></div>
          </div>
        </div>
      </div>
    </>
  )
}
